import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";

import { NoDataMessage, SpinLoader } from "@/components/atoms";
import { insuredGoodApi } from "@/shared/apis";
import { defaultInsuredGoodPagination, InsuredGoodCategory, InsuredGoodCategoryType } from "@/shared/domain";
import { useAuth, useGlobalFilter, useNavigation } from "@/shared/hooks";
import { StyledCategoriesPanel, StyledItemsPanel } from "@/styles/styled";
import * as tagManager from "@/tagManager";

import InsuredGoodCategoryCard from "../components/InsuredGoodCategoryCard/InsuredGoodCategoryCard";
import InsuredGoodItem from "../components/InsuredGoodItem/InsuredGoodItem";
import { useInsuredGoodsQueries } from "../utils";

const defaultPage = { pageNumber: 1, pageSize: 5 };

const MAX_INSURED_GOODS = 4;

const InsuredGoodPanel: FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { navToInsuredGoods } = useNavigation();
    const { policyholdersGF } = useGlobalFilter();

    const { useInsuredGoodsCategoriesQuery } = useInsuredGoodsQueries();

    const filteredPolicyholders = policyholdersGF.filter(
        (policyholder) => user.getAccessRightFor(policyholder)?.canViewRisks,
    );
    const invalidPolicyholders = !filteredPolicyholders.length;

    const request = {
        policyholdersIdList: filteredPolicyholders,
        category: InsuredGoodCategoryType.ALL,
        ...defaultPage,
    };

    const {
        data: insuredGoods = defaultInsuredGoodPagination,
        isLoading: isLoadingInsuredGoods,
        isFetched: isFetchedInsuredGoods,
        error: insuredGoodError,
    } = useQuery(["insuredGoods", request], () => insuredGoodApi.getInsuredGoods(request), {
        keepPreviousData: true,
        // suspense: false,
        enabled: !invalidPolicyholders,
    });

    const displayCategoryView = insuredGoods.result.length > MAX_INSURED_GOODS;

    const { insuredGoodCategories, isLoadingCategories, error } = useInsuredGoodsCategoriesQuery({
        enabled: displayCategoryView && !invalidPolicyholders,
    });

    if (error) {
        throw error;
    }
    if (insuredGoodError) {
        throw insuredGoodError;
    }
    const getResults = () => {
        // Category display
        if (!invalidPolicyholders && displayCategoryView && insuredGoodCategories.length > 0) {
            const defaultCategory = {
                totalItems: insuredGoodCategories.map((x) => x.totalItems).reduce((a, b) => a + b, 0),
                category: { id: "0", name: t("Common.All") },
            } as InsuredGoodCategory;

            return (
                <StyledCategoriesPanel $isLoading={isLoadingCategories}>
                    {insuredGoodCategories.length > 1 && (
                        <InsuredGoodCategoryCard
                            category={defaultCategory}
                            onCategoryClick={() => navToInsuredGoods(defaultCategory.category.id)}
                        />
                    )}
                    {insuredGoodCategories.map((insuredGoodCategory) => (
                        <InsuredGoodCategoryCard
                            key={`insuredGoodCategory-${insuredGoodCategory.category.id}`}
                            category={insuredGoodCategory}
                            onCategoryClick={() => navToInsuredGoods(insuredGoodCategory.category.id)}
                        />
                    ))}
                </StyledCategoriesPanel>
            );
        }

        if (isLoadingInsuredGoods || isLoadingCategories || !isFetchedInsuredGoods) {
            return <SpinLoader />;
        }

        // InsuredGood display
        if (!invalidPolicyholders && !displayCategoryView && insuredGoods.result.length > 0) {
            tagManager.pushViewItemListEvent("insuredGood", "Insured Goods", true);

            return (
                <StyledItemsPanel>
                    {insuredGoods.result.map((insuredGood) => (
                        <InsuredGoodItem key={`insuredGoodItem-${insuredGood.id}`} insuredGood={insuredGood} />
                    ))}
                </StyledItemsPanel>
            );
        }

        return <NoDataMessage>{t("InsuredGoods.Empty")}</NoDataMessage>;
    };

    return <div>{getResults()}</div>;
};

export default InsuredGoodPanel;
